/**
 * Where each surface lives. Named ONCE, here, and imported everywhere else.
 *
 * THE BUG THIS EXISTS FOR. The free tools used to be served at `/tools/` on the marketing
 * origin. They moved to their own host, and three things were updated — vite's `base`, the
 * CloudFront distribution, the DNS record — while two were not: the router's base, and 29
 * hardcoded `/tools/...` hrefs spread across all three apps.
 *
 * Every one of those 29 broke, and not one of them reported it:
 *
 *   - on landing, `/tools/grader` hit a distribution whose 404 is `200 /index.html`, so the
 *     homepage's own hero CTA answered 200 and client-routed to "That page does not exist";
 *   - in gtm, the stale `<Router base="/tools">` re-added the prefix on every pushState, so
 *     clicking a tool rewrote the URL to `<tools host>/tools/grader` — which still rendered,
 *     because the base was stripped again on reload. The bug repaired itself on every load and
 *     left a wrong, non-canonical URL in the bar and in everything anyone copied.
 *
 * A string literal is a guess about somebody else's deployment. These are the answer, and
 * `scripts/crosslinks.mjs` fails the build on any root-relative href that crosses a surface.
 *
 * Overridable per-build so `npm run dev` can point a surface at a local port. Unset — which is
 * every production build — they are the real hosts.
 */

import surfaces from "./surfaces.json";

const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};

/** The marketing site. Apex, and the only surface that is prerendered per-route from a ROUTES list. */
export const PRODUCT = (env.VITE_PRODUCT_ORIGIN ?? surfaces.origins.landing).replace(/\/$/, "");

/**
 * The eleven free tools. SAME ORIGIN as the marketing site, mounted at /tools.
 *
 * They had their own host for exactly one day. The move off the path prefix was made because
 * the prefix cost four separate CloudFront failures, and every one of those four is now dead:
 * the router function it needed exists and is unit-tested against the real built output
 * (scripts/cdn-routes.mjs), the IAM tag conditions that silently excluded CloudFront Functions
 * were fixed, landing's 403-to-index error response that swallowed the tools' real 404s was
 * replaced with a genuine 404, and the fourth — a bucket policy that has to name every
 * distribution — stops existing when there is one distribution instead of two.
 *
 * What the subdomain could not buy back is the reason to be on the apex at all: these eleven
 * pages are the acquisition surface, they are the pages most likely to earn a link, and that
 * authority is worth more pointed at the host that sells the product than at a subdomain of it.
 *
 * STILL A SEPARATE BUILD. Same origin does not make a root-relative link between the two
 * surfaces safe — landing's client router would intercept `/tools/grader` and resolve it
 * against its own route table. Cross-surface links use this constant, absolute.
 */
export const TOOLS = (env.VITE_TOOLS_ORIGIN ?? `${PRODUCT}${surfaces.bases.gtm}`).replace(/\/$/, "");

/** The operator console, same-origin with the API so a tenant key never crosses an origin. */
export const CONSOLE_ORIGIN = (env.VITE_CONSOLE_ORIGIN ?? surfaces.origins.console).replace(/\/$/, "");

/** The console app itself, which is served under `/console` on that origin. */
export const CONSOLE = `${CONSOLE_ORIGIN}${surfaces.bases.console}`;

/** Docs, the open spec, and the in-browser verifier. Static multi-page, not an SPA. */
export const DOCS = (env.VITE_DOCS_ORIGIN ?? surfaces.origins.docs).replace(/\/$/, "");

/**
 * The path prefix each surface is mounted under on its origin, '' for one at the root.
 *
 * `<Router base={BASE_OF.gtm}>` rather than `<Router base="/tools">` on purpose. A literal here
 * is the second copy of the mount point, and the second copy is the one that does not get
 * updated: that is precisely how gtm ended up with a `/tools` base after the tools had moved off
 * `/tools`, which re-added the prefix on every pushState and stripped it again on every load —
 * a bug that repaired itself on each page view and only ever surfaced as a wrong URL in the bar.
 */
export const BASE_OF: Record<"landing" | "gtm" | "console", string> =
  surfaces.bases as Record<"landing" | "gtm" | "console", string>;

/**
 * Which surface owns a root-relative path — the single source of truth for the crosslinks
 * check, for gtm's cross-surface redirect, and for the sitemaps.
 *
 * `/contact` is deliberately absent: BOTH public surfaces have one, each posting with its own
 * `source` so the leads table can tell them apart. A relative link is correct on either, which
 * makes it the one path nothing may have an opinion about.
 */
export const OWNED: Record<"landing" | "gtm" | "console", readonly string[]> =
  surfaces.owned as Record<"landing" | "gtm" | "console", readonly string[]>;

/**
 * The ORIGIN each surface answers on — no path, ever.
 *
 * A redirect is built as `ORIGIN_OF[owner] + path`, and the path already carries the owner's
 * base (that is what `owned` holds). Folding /tools into this map would send anyone leaving the
 * tools for the tools to `/tools/tools/...`.
 */
export const ORIGIN_OF = {
  landing: PRODUCT,
  gtm: PRODUCT,
  console: CONSOLE_ORIGIN,
} as const;

/**
 * The surface that owns a path, or null if the app serving it does.
 *
 * Matches on the FIRST SEGMENT, so `/console/agents` resolves to console the same as
 * `/console` does.
 */
export function ownerOf(path: string): keyof typeof ORIGIN_OF | null {
  const seg = "/" + path.split(/[/?#]/).filter(Boolean)[0];
  for (const [app, paths] of Object.entries(OWNED)) {
    if (paths.includes(seg)) return app as keyof typeof ORIGIN_OF;
  }
  return null;
}
